import React, { useEffect, useState } from "react";
import Datatable from "../../EntryFile/datatable";
import { Form, Card, Modal } from "react-bootstrap";
import alertify from "alertifyjs";
import Tabletop from "../../EntryFile/tabletop";
import { fetchUsers, getAllHistoryLogs } from "../../core/api/user";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { X } from "lucide-react";

const HistoryLogs = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [search, setSearch] = useState("");
    const [pageSize] = useState(10);
    const [filters, setFilters] = useState({
        entityType: "",
        source: "",
        startDate: null,
        endDate: null,
        userId: ""
    });
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const entityTypes = ["User", "Patient", "Appointment", "Employee", "Inventory", "ContactEnquiry"];
    const sources = ["super_admin", "user", "public_website"];

    // Add function to fetch users
    const fetchAllUsers = async () => {
        setLoadingUsers(true);
        try {
            const result = await fetchUsers({ page: 1, limit: 1000, search: "" });
            setUsers(result?.data?.users || []);
        } catch (error) {
            console.error(error);
            alertify.error("Failed to fetch users");
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        fetchAllUsers();
    }, []);


    // Fetch history logs from the API
    const fetchData = async ({ page, pageSize }) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit: pageSize,
                search: search || "",
                ...(filters.entityType && { entityType: filters.entityType }),
                ...(filters.source && { source:filters.source }),
                ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
                ...(filters.endDate && { endDate: filters.endDate.toISOString() }),
                ...(filters.userId && { userId: filters.userId }),
            });

            const result = await getAllHistoryLogs(queryParams);
            setData(result.data.logs);
            setTotalItems(result.data.totalLogs);
        } catch (error) {
            console.error(error);
            alertify.error("Failed to fetch history logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ page: 1, pageSize });
    }, [search, filters]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Define columns for the history logs table
    const columns = [
        {
            title: "User",
            render: (record) => (
                record.user ? `${record.user.firstName} ${record.user.surname}` : "System"
            ),
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (action) => <span style={{ textTransform: "capitalize" }}>{action}</span>,
            sorter: (a, b) => a.action.localeCompare(b.action),
        },
        {
            title: "Entity Type",
            dataIndex: "entityType",
            sorter: (a, b) => a.entityType.localeCompare(b.entityType),
        },
        {
            title: "Entity ID",
            dataIndex: "entityId",
        },

        {
            title: "Source",
            dataIndex: "source",
            sorter: (a, b) => a.source.localeCompare(b.source),
        },

        {
            title: "Date",
            dataIndex: "createdAt",
            render: (date) => new Date(date).toLocaleString(),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="page-header">
                    <div className="page-title">
                        <h4>History Logs</h4>
                        <h6>View all system activities</h6>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        {/* Search Bar */}
                        <div className="row">
                            <Tabletop
                                OnChange={(e) => setSearch(e.target.value)}
                                value={search}
                                placeholder="Search in all fields..."
                            />
                        </div>
                        {/* Filters */}
                        <div className="row g-3 mb-4"> {/* Add g-3 for gutters */}
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <Form.Group>
                                    <Form.Label>User</Form.Label>
                                    <Form.Select
                                        className="form-control w-100"
                                        value={filters.userId}
                                        onChange={(e) => handleFilterChange("userId", e.target.value)}
                                        disabled={loadingUsers}
                                    >
                                        <option value="">All Users</option>
                                        {users.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {`${user?.firstName || ''} ${user?.surname || ''}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {loadingUsers && <div className="text-muted small">Loading users...</div>}
                                </Form.Group>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <Form.Group>
                                    <Form.Label>Entity Type</Form.Label>
                                    <Form.Select
                                        className="form-control w-100"
                                        value={filters.entityType}
                                        onChange={(e) => handleFilterChange("entityType", e.target.value)}
                                    >
                                        <option value="">All Entities</option>
                                        {entityTypes.map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <Form.Group>
                                    <Form.Label>Source</Form.Label>
                                    <Form.Select
                                        className="form-control w-100"
                                        value={filters.source}
                                        onChange={(e) => handleFilterChange("source", e.target.value)}
                                    >
                                        <option value="">All Sources</option>
                                        {sources.map((source) => (
                                            <option key={source} value={source}>
                                                {source}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                        selected={filters.startDate}
                                        onChange={(date) => handleFilterChange("startDate", date)}
                                        className="form-control w-100"
                                        dateFormat="yyyy-MM-dd"
                                        isClearable
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <DatePicker
                                        selected={filters.endDate}
                                        onChange={(date) => handleFilterChange("endDate", date)}
                                        className="form-control w-100"
                                        dateFormat="yyyy-MM-dd"
                                        isClearable
                                    />
                                </Form.Group>
                            </div>
                        </div>



                        {/* History Logs Table */}
                        <div className="table-responsive">
                            <Datatable
                                isLoading={loading}
                                columns={columns}
                                response={{
                                    data: data?.map((item) => ({ ...item })),
                                    totalDataCount: totalItems,
                                }}
                                fetchData={fetchData}
                                NoPagination={false}
                                onRowClick={(record) => {
                                    setSelectedLog(record);
                                    setShowDetailsModal(true);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                show={showDetailsModal}
                onHide={() => setShowDetailsModal(false)}
                centered
                size="lg"
            >
                <Modal.Header>
                    <Modal.Title>History Log Details</Modal.Title>
                    <button
                        type="button"
                        className="btn-close me-1"
                        onClick={() => setShowDetailsModal(false)}
                    >
                        <X size={20} onClick={() => setShowDetailsModal(false)} />
                    </button>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <div className="history-details">
                            <div className="mb-3">
                                <strong>User:</strong> {selectedLog.user ?
                                    `${selectedLog.user.firstName} ${selectedLog.user.surname}` :
                                    "System"}
                            </div>
                            <div className="mb-3">
                                <strong>Action:</strong> <span className="text-capitalize">{selectedLog.action}</span>
                            </div>
                            <div className="mb-3">
                                <strong>Entity Type:</strong> {selectedLog.entityType}
                            </div>
                            <div className="mb-3">
                                <strong>Entity ID:</strong> {selectedLog.entityId}
                            </div>
                            <div className="mb-3">
                                <strong>Source:</strong> {selectedLog.source}
                            </div>
                            <div className="mb-3">
                                <strong>Date:</strong> {new Date(selectedLog.createdAt).toLocaleString()}
                            </div>
                            <div>
                                <strong>Details:</strong>
                                <pre className="mt-2 bg-light p-3 rounded">
                                    {JSON.stringify(JSON.parse(selectedLog.details || "{}"), null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowDetailsModal(false)}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default HistoryLogs;